<script setup>
import VDevider from "@/Shared/VDevider.vue";
import VButtonSubmit from "@/Shared/Buttons/VButtonSubmit";
import VInputWithLabel from "../../Form/VInputWithLabel.vue";

import { useForm } from "@inertiajs/vue3";
import VRadioWithBlockLabel from "../../Form/Survey/VRadioWithBlockLabel.vue";
import VInputWithBlockLabel from "../../Form/Survey/VInputWithBlockLabel.vue";
import intus from "intus";
import { isRequired, isInteger, isMin } from "intus/rules";
import { computed, watch } from "vue";
import { getIntValue } from "../../../Helpers/number";

import { scrollOnError } from "@/Helpers/validation";

const props = defineProps({
    additional: Object,
    isShowSubmit: {
        Type: Boolean,
        default: true,
    },
});

const { surveyFields, answers } = props.additional;

const form = useForm({
    what_is_your_travel_arrangement_to_sabah:
        answers?.what_is_your_travel_arrangement_to_sabah,
    please_indicate_the_amount_of_people_covered_for_the_overall_travel_spending:
        answers?.please_indicate_the_amount_of_people_covered_for_the_overall_travel_spending,
    flights: answers?.flights ?? 0,
    accommodation: answers?.accommodation ?? 0,
    food_and_beverages: answers?.food_and_beverages ?? 0,
    activities: answers?.activities ?? 0,
    events: answers?.events ?? 0,
    transportation: answers?.transportation ?? 0,
    optional_tours: answers?.optional_tours ?? 0,
    fuel: answers?.fuel ?? 0,
    shopping: answers?.shopping ?? 0,
    local_transfer_transportation: answers?.local_transfer_transportation ?? 0,
    vehicle_rental: answers?.vehicle_rental ?? 0,
    sports: answers?.sports ?? 0,
    healthmedical_treatment: answers?.healthmedical_treatment ?? 0,
    wellness: answers?.wellness ?? 0,
    others_lump_sum: answers?.others_lump_sum ?? "",
});

const emits = defineEmits("onNext");

const handleClickNext = () => {
    const validation = intus.validate(form.data(), {
        what_is_your_travel_arrangement_to_sabah: [isRequired()],
        please_indicate_the_amount_of_people_covered_for_the_overall_travel_spending:
            [isRequired()],
        flights: [isRequired(), isInteger()],
        accommodation: [isRequired(), isInteger()],
        food_and_beverages: [isRequired(), isInteger()],
        activities: [isRequired(), isInteger()],
        events: [isRequired(), isInteger()],
        transportation: [isRequired(), isInteger()],
        optional_tours: [isRequired(), isInteger()],
        fuel: [isRequired(), isInteger()],
        shopping: [isRequired(), isInteger()],
        local_transfer_transportation: [isRequired(), isInteger()],
        vehicle_rental: [isRequired(), isInteger()],
        sports: [isRequired(), isInteger()],
        healthmedical_treatment: [isRequired(), isInteger()],
        wellness: [isRequired(), isInteger()],
        others_lump_sum: [isRequired(), isInteger(), isMin(1)],
    });

    form.clearErrors();
    if (validation.passes()) {
        emits("onNext", form.data());
    } else {
        form.setError(validation.errors());
        setTimeout(() => scrollOnError(), 300);
    }
};

const detailKey = [
    "flights",
    "accommodation",
    "food_and_beverages",
    "activities",
    "events",
    "transportation",
    "optional_tours",
    "fuel",
    "shopping",
    "local_transfer_transportation",
    "vehicle_rental",
    "sports",
    "healthmedical_treatment",
    "wellness",
];

const isLumpSum = computed(() => {
    let total = 0;
    for (const textKey of detailKey) {
        total += getIntValue(form[textKey]);
    }

    if (total > 0) {
        form.others_lump_sum = total;
    }

    return total > 0 ? false : true;
});
</script>

<template>
    <div>
        <div class="row">
            <div class="col-12">
                <div class="mb-3">
                    <VRadioWithBlockLabel
                        elId="what_is_your_travel_arrangement_to_sabah"
                        v-model:value="
                            form.what_is_your_travel_arrangement_to_sabah
                        "
                        :label="
                            surveyFields[
                                'what_is_your_travel_arrangement_to_sabah'
                            ]?.name
                        "
                        :options="
                            surveyFields[
                                'what_is_your_travel_arrangement_to_sabah'
                            ]?.options.map((value) => {
                                return { id: value, description: value };
                            })
                        "
                        :error="
                            form.errors
                                ?.what_is_your_travel_arrangement_to_sabah
                        "
                    />
                </div>
                <div class="mb-3">
                    <VInputWithBlockLabel
                        elId="please_indicate_the_amount_of_people_covered_for_the_overall_travel_spending"
                        v-model:value="
                            form.please_indicate_the_amount_of_people_covered_for_the_overall_travel_spending
                        "
                        :widthLabel="4"
                        :widthInput="8"
                        type="number"
                        :label="
                            surveyFields[
                                'please_indicate_the_amount_of_people_covered_for_the_overall_travel_spending'
                            ]?.name
                        "
                        :error="
                            form.errors
                                ?.please_indicate_the_amount_of_people_covered_for_the_overall_travel_spending
                        "
                    />
                </div>
                <VDevider />
                <div class="mb-4">
                    <p class="fw-bold mb-3">
                        What are your rough estimates of expenses for these
                        categories? (includes package tours/self-arranged)
                    </p>

                    <div class="mb-1">
                        <VInputWithLabel
                            elId="flights"
                            v-model:value="form.flights"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['flights']?.name"
                            :error="form.errors?.flights"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="accommodation"
                            v-model:value="form.accommodation"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['accommodation']?.name"
                            :error="form.errors?.accommodation"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="food_and_beverages"
                            v-model:value="form.food_and_beverages"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['food_and_beverages']?.name"
                            :error="form.errors?.food_and_beverages"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="activities"
                            v-model:value="form.activities"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['activities']?.name"
                            :error="form.errors?.activities"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="events"
                            v-model:value="form.events"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['events']?.name"
                            :error="form.errors?.events"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="transportation"
                            v-model:value="form.transportation"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['transportation']?.name"
                            :error="form.errors?.transportation"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="optional_tours"
                            v-model:value="form.optional_tours"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['optional_tours']?.name"
                            :error="form.errors?.optional_tours"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="fuel"
                            v-model:value="form.fuel"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['fuel']?.name"
                            :error="form.errors?.fuel"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="shopping"
                            v-model:value="form.shopping"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['shopping']?.name"
                            :error="form.errors?.shopping"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="local_transfer_transportation"
                            v-model:value="form.local_transfer_transportation"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="
                                surveyFields['local_transfer_transportation']
                                    ?.name
                            "
                            :error="form.errors?.local_transfer_transportation"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="vehicle_rental"
                            v-model:value="form.vehicle_rental"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['vehicle_rental']?.name"
                            :error="form.errors?.vehicle_rental"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="sports"
                            v-model:value="form.sports"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['sports']?.name"
                            :error="form.errors?.sports"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="healthmedical_treatment"
                            v-model:value="form.healthmedical_treatment"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="
                                surveyFields['healthmedical_treatment']?.name
                            "
                            :error="form.errors?.healthmedical_treatment"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="wellness"
                            v-model:value="form.wellness"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['wellness']?.name"
                            :error="form.errors?.wellness"
                            :additionalAttr="{
                                disabled:
                                    isLumpSum &&
                                    (form.others_lump_sum != '' ||
                                        form.others_lump_sum != 0),
                            }"
                        />
                    </div>
                    <div class="mb-1">
                        <VInputWithLabel
                            elId="others_lump_sum"
                            v-model:value="form.others_lump_sum"
                            :widthLabel="4"
                            :widthInput="8"
                            type="number"
                            :label="surveyFields['others_lump_sum']?.name"
                            :error="form.errors?.others_lump_sum"
                            :additionalAttr="{
                                disabled: !isLumpSum,
                            }"
                        />
                    </div>
                </div>
            </div>
        </div>

        <VDevider />
        <div class="text-end" v-if="isShowSubmit">
            <VButtonSubmit type="button" @onCLickSubmit="handleClickNext">
                Next
            </VButtonSubmit>
        </div>
    </div>
</template>
